import { falSlidersSimple } from 'shared/@generated/icons/falSlidersSimple';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, TouchableOpacity, View } from 'react-native';
import { Props, Breeder, Paginator } from 'shared/@types';
import { City } from 'shared/library/cities';
import { prepareDataForApi } from 'shared/library/api-client';
import { useTranslation } from 'react-i18next';
import { apiRoute } from 'shared/library/api-route';
import { BreederCard } from 'shared/components/breeder-card';
import { Pagination } from 'shared/components/pagination';
import { H1 } from 'shared/components/title';
import { Text } from 'shared/components/text';
import { Button } from 'shared/components/button';
import { FullscreenModal } from 'shared/components/fullscreen-modal';
import { BreedMultiSelect } from 'shared/components/form/breed-multi-select';

import {
  BreedersFilter,
  BreedersFilterProvider,
  getBreedersApiParams,
  parseBreedersFilter,
  useBreedersFilterStore,
} from 'shared/state/breeders-filter';
import { useDebouncedQueryFilter } from 'shared/library/use-debounced-query-filter';
import { PageBuilder } from 'shared/library/page-builder';
import { PageContent } from 'shared/components/page-content';
import { CardMediaPlaceholder } from 'shared/components/card-media-placeholder';
import { BreedersWebFilterControls } from 'shared/components/breeders-web-filter-controls';
import { Animal } from 'shared/@generated/models';
import { useRouter } from 'shared/library/use-router';
import { AdAnimalProvider } from 'shared/components/ad-banner/ad-animal-context';
import { resolveAdAnimal } from 'shared/components/ad-banner/ad-animal';
import useBreedsStore from 'shared/state/breeds';

interface IndexBreedersListScreenProps extends Props {
  initialBreeders: Paginator<Breeder>;
  initialFilter: BreedersFilter;
  loadingInitial?: boolean;
  lockedCity?: City;
  breedAnimal?: Animal;
  title?: string;
  subtitle?: string;
  showFilter?: boolean;
}

const IndexBreedersListScreen = ({
  initialBreeders,
  initialFilter,
  loadingInitial,
  lockedCity,
  breedAnimal,
  title,
  subtitle,
  showFilter = true,
}: IndexBreedersListScreenProps) => {
  const adAnimal = useBreedsStore((state) =>
    resolveAdAnimal({
      animal: breedAnimal,
      breedIds: initialFilter.breeds,
      breeds: state.breeds,
    }),
  );

  return (
    <AdAnimalProvider animal={adAnimal}>
      <BreedersFilterProvider initialState={initialFilter}>
        <ScreenContent
          initialBreeders={initialBreeders}
          initialFilter={initialFilter}
          loadingInitial={loadingInitial}
          lockedCity={lockedCity}
          breedAnimal={breedAnimal}
          title={title}
          subtitle={subtitle}
          showFilter={showFilter}
        />
      </BreedersFilterProvider>
    </AdAnimalProvider>
  );
};

const ScreenContent = ({
  initialBreeders,
  initialFilter,
  loadingInitial = false,
  lockedCity,
  breedAnimal,
  title,
  subtitle,
  showFilter = true,
}: IndexBreedersListScreenProps) => {
  const { t } = useTranslation('screens');
  const setFilter = useBreedersFilterStore((state) => state.setFilter);
  const reset = useBreedersFilterStore((state) => state.reset);
  const filter = useBreedersFilterStore((state) => state.filter);
  const appliedCount = useBreedersFilterStore((state) => state.appliedCount);
  const [breeders, setBreeders] = useState(initialBreeders);
  const [nativeFilterOpen, setNativeFilterOpen] = useState(false);
  const showResultCount =
    filter.breeds.length === initialFilter.breeds.length &&
    filter.breeds.every((breed) => initialFilter.breeds.includes(breed));
  // Keep native modal edits local until its close/apply action.
  const routeFilterRef = useRef(filter);
  if (!nativeFilterOpen) routeFilterRef.current = filter;
  const { asPath, replace } = useRouter();

  useDebouncedQueryFilter(initialFilter, routeFilterRef.current, (isLocalNavigation) => {
    if (!isLocalNavigation && !nativeFilterOpen) {
      setFilter(initialFilter);
    }

    setBreeders(initialBreeders);
  });

  useEffect(() => {
    if (Platform.OS !== 'web' || !breedAnimal) {
      return;
    }

    const [pathWithQuery = '', hash = ''] = asPath.split('#');
    const [pathname = '', currentQuery = ''] = pathWithQuery.split('?');
    const params = new URLSearchParams(currentQuery);

    if (!params.has('animal')) {
      return;
    }

    params.delete('animal');

    const query = params.toString();
    const destination = `${query ? `${pathname}?${query}` : pathname}${hash ? `#${hash}` : ''}`;

    void replace(destination, undefined);
  }, [asPath, breedAnimal, replace]);

  return (
    <PageContent className={'flex-1 !pt-0'} contentClassName={'flex-1'} nativeFlushTop={true}>
      <Pagination
        nativeSafeTop={true}
        showAds={true}
        gap={3}
        cols={2}
        mdCols={3}
        lgCols={4}
        mdGap={6}
        loadingInitial={loadingInitial}
        loadingPlaceholderCount={4}
        paginator={breeders}
        onPageChange={(page) => setFilter({ page })}
        renderItem={(breeder) => <BreederCard key={breeder.id} breeder={breeder} />}
        renderLoadingPlaceholder={(index) => (
          <LoadingBreederCard key={`loading-breeder-${index}`} />
        )}
        ListHeader={() => (
          <>
            <H1
              title={
                title ??
                (lockedCity
                  ? t('breeders.index_breeders_list.locked_city_title', {
                      city: lockedCity.name,
                    })
                  : t('breeders.index_breeders_list.title'))
              }
              subtitle={
                subtitle ??
                (lockedCity
                  ? t('breeders.index_breeders_list.locked_city_description', {
                      city: lockedCity.name,
                    })
                  : t('breeders.index_breeders_list.description'))
              }
              marginBottom={Platform.OS !== 'web'}
              hideTitleWhenNativeHeader={true}
            />

            {showFilter && (
              <>
                {Platform.OS === 'web' ? (
                  <BreedersWebFilterControls totalBreeders={breeders.total} animal={breedAnimal} />
                ) : (
                  <View className={'flex-row items-center gap-3 md:flex-wrap'}>
                    <FullscreenModal
                      headerTitle={t('components:breeders_filter_form.filter_breeders')}
                      contentTestID={'breeders-filter-modal'}
                      onClose={() => setNativeFilterOpen(false)}
                      headerRight={() => (
                        <TouchableOpacity
                          className={'p-2'}
                          testID={'breeders-filter-clear'}
                          onPress={reset}
                        >
                          <Text className={'text-sm font-bold text-red-700'}>
                            {t('common:clear')}
                          </Text>
                        </TouchableOpacity>
                      )}
                      trigger={({ open }) => (
                        <Button
                          icon={falSlidersSimple}
                          iconPosition={'left'}
                          text={t('components:breeders_filter_form.filter')}
                          color={'white'}
                          dense={true}
                          testID={'breeders-filter-trigger'}
                          onPress={() => {
                            setNativeFilterOpen(true);
                            open();
                          }}
                        >
                          <Text
                            className={
                              'bg-brand-cream-dark text-brand-brown-dark ml-1.5 w-5 rounded py-0.5 text-center text-xs font-bold'
                            }
                          >
                            {appliedCount}
                          </Text>
                        </Button>
                      )}
                      footer={({ close }) => (
                        <Button
                          text={t('components:breeders_filter_form.view_breeders')}
                          center={true}
                          testID={'breeders-filter-apply'}
                          onPress={close}
                        >
                          {showResultCount && (
                            <Text
                              className={
                                'ml-3 rounded bg-black/10 p-1 text-sm font-black text-white'
                              }
                            >
                              {breeders.total}
                            </Text>
                          )}
                        </Button>
                      )}
                    >
                      <View className={'gap-1.5'} testID={'breeders-filter-form'}>
                        <BreedMultiSelect
                          value={filter.breeds}
                          onChange={(breeds) => setFilter({ breeds })}
                          animal={breedAnimal}
                          historyEnabled={false}
                          allowQuery={false}
                          placeholder={t('components:breeders_filter_form.breed_placeholder')}
                        />
                      </View>
                    </FullscreenModal>
                  </View>
                )}
              </>
            )}
          </>
        )}
      />
    </PageContent>
  );
};

function LoadingBreederCard() {
  return (
    <View className={'bg-white'}>
      <CardMediaPlaceholder
        className={'aspect-4/3 w-full rounded-t'}
        iconClassName={'text-brand-brown-dark/30 size-9'}
        panelClassName={'bg-brand-cream-dark/15 rounded-3xl p-3'}
      />
      <View className={'border-brand-brown-dark/15 rounded-b border border-t-0 p-3'}>
        <View className={'bg-brand-cream h-6 w-3/4 rounded'} />
        <View className={'bg-brand-cream mt-2 h-5 w-1/3 rounded'} />
      </View>
    </View>
  );
}

IndexBreedersListScreen.pageBuilder = new PageBuilder<
  IndexBreedersListScreenProps,
  BreedersFilter & { latitude?: number; longitude?: number; country?: string }
>().request(({ req, params }) =>
  req<{ breeders: Paginator<Breeder> }>(apiRoute('breeders.index'), {
    ...prepareDataForApi(getBreedersApiParams(params)),
    with_details: true,
  }).then((data) => ({
    initialBreeders: data.breeders,
    initialFilter: parseBreedersFilter(params),
  })),
);

export { IndexBreedersListScreen };
export type { IndexBreedersListScreenProps };
